import React, { useContext, useState, useEffect } from 'react'

import { RouteComponentProps } from 'react-router-dom'
import WebFont from 'webfontloader'

import Data from '../../api/Data'
import { IProject, IClip, IEpisode } from '../../api/Data'
import { GlobalContext } from '../../contexts/GlobalContext'

import Section from '../../components/common/Section'

import ConfigureTab from './configure/ConfigureTab'
import CollectTab from './collect/CollectTab'
import CollaborateTab from './collaborate/CollaborateTab'
import CurateTab from './curate/CurateTab'
import CustomizeTab from './customize/CustomizeTab'
import CompileTab from './compile/CompileTab'
import ClaimTab from './claim/ClaimTab'
import CombineTab from './combine/CombineTab'

const ProjectShowPage = ({ history, match, location }: IProps) => {
  const { hasUserMadeChange, dispatchGlobalContext } = useContext(GlobalContext)

  // States
  const [projectTitle, setProjectTitle] = useState<string>('')
  const [project, setProject] = useState<IProject | null>(null)
  const [theme, setTheme] = useState<any>(null)
  const [episodes, setEpisodes] = useState<IEpisode[] | null>(null)
  const [clipsSelected, setClipsSelected] = useState<IClip[]>([])

  // Fetch data
  useEffect(() => {
    const fetchProject = async () => {
      const slug = match.params.projectSlug

      const { data, error } = await Data.getProject(slug)

      if (error) return alert('Oops something went wrong')

      const { clips: projectClips, episodes: projectEpisodes, theme: projectTheme, ...projectData } = data

      setProjectTitle(projectData.name)
      setProject(projectData)
      setTheme(projectTheme)
      setEpisodes(projectEpisodes)
    }
    fetchProject()
  }, [match.params.projectSlug, location.search])

  // Fetch project font
  useEffect(() => {
    const fetchProjectFont = () => {
      const fontToFetch = project?.displaySetting.primaryFont

      return WebFont.load({
        google: {
          families: [`${fontToFetch}:400,700,italic,bold,bolditalic`],
        },
      })
    }

    if (project) fetchProjectFont()
  }, [project])

  // Exit the project
  const exitProject = () => {
    if (hasUserMadeChange) {
      return dispatchGlobalContext({ type: 'set exit without saving modal', payload: `/projects` })
    }

    return history.push('/projects')
  }

  if (!project || !theme || !episodes) return null

  const { section } = match.params


  return (
    <Section>
      <>
        {section === 'configure' && (
          <ConfigureTab
            projectTitle={projectTitle}
            project={project}
            setProject={setProject}
            theme={theme}
            setTheme={setTheme}
            exitProject={exitProject}
          />
        )}
        {section === 'collect' && (
          <CollectTab
            projectTitle={projectTitle}
            project={project}
            theme={theme}
            setProject={setProject}
            setTheme={setTheme}
            exitProject={exitProject}
          />
        )}
        {section === 'collaborate' && (
          <CollaborateTab
            projectTitle={projectTitle}
            project={project}
            theme={theme}
            setProject={setProject}
            setTheme={setTheme}
            exitProject={exitProject}
          />
        )}
        {section === 'curate' && (
          <CurateTab
            projectTitle={projectTitle}
            project={project}
            clipsSelected={clipsSelected}
            setClipsSelected={setClipsSelected}
            exitProject={exitProject}
          />
        )}
        {section === 'combine' && (
          <CombineTab
            projectTitle={projectTitle}
            theme={theme}
            setTheme={setTheme}
            clipsSelected={clipsSelected}
            setClipsSelected={setClipsSelected}
          />
        )}
        {section === 'customize' && (
          <CustomizeTab
            projectTitle={projectTitle}
            project={project}
            setProject={setProject}
            theme={theme}
            setTheme={setTheme}
            exitProject={exitProject}
          />
        )}
        {section === 'compile' && (
          <CompileTab
            projectTitle={projectTitle}
            project={project}
            episodes={episodes}
            setEpisodes={setEpisodes}
            exitProject={exitProject}
          />
        )}
        {section === 'claim' && (
          <ClaimTab
            projectTitle={projectTitle}
            exitProject={exitProject}
          />
        )}
      </>
    </Section>
  )
}

interface IProps extends RouteComponentProps<any> {}

export default ProjectShowPage
